/**
 * GLSL snippets shared by the map shaders. The coast function lives here so the terrain shader
 * and the territory/fog overlay agree on exactly the same organic shoreline.
 */
import { HEX_SIZE, HEX_W } from '@friction/engine';

const f = (n: number): string => (Number.isInteger(n) ? `${n}.0` : `${n}`);

/** `0xRRGGBB` → `vec3(r, g, b)` literal. */
export function glslColor(c: number): string {
  const r = ((c >> 16) & 255) / 255;
  const g = ((c >> 8) & 255) / 255;
  const b = (c & 255) / 255;
  return `vec3(${r.toFixed(4)}, ${g.toFixed(4)}, ${b.toFixed(4)})`;
}

/** Hashing, sampling and hex-lattice helpers (needs no uniforms). */
export const GLSL_COMMON = /* glsl */ `
const float HEX_SIZE = ${f(HEX_SIZE)};
const float HEX_W = ${f(HEX_W)};

// Dave Hoskins' "hash without sine" — stable for the large lattice coordinates we feed it.
vec2 hash22(vec2 p) {
  vec3 p3 = fract(vec3(p.xyx) * vec3(0.1031, 0.1030, 0.0973));
  p3 += dot(p3, p3.yzx + 33.33);
  return fract((p3.xx + p3.yz) * p3.zy);
}
float hash12(vec2 p) {
  vec3 p3 = fract(vec3(p.xyx) * 0.1031);
  p3 += dot(p3, p3.yzx + 33.33);
  return fract((p3.x + p3.y) * p3.z);
}

vec4 cubicW(float v) {
  vec4 n = vec4(1.0, 2.0, 3.0, 4.0) - v;
  vec4 s = n * n * n;
  float x = s.x;
  float y = s.y - 4.0 * s.x;
  float z = s.z - 4.0 * s.y + 6.0 * s.x;
  return vec4(x, y, z, 6.0 - x - y - z) * (1.0 / 6.0);
}
// Cubic B-spline filtering from 4 bilinear taps: C2-smooth fields, so contour lines and
// hillshade show no texel facets even at maximum zoom.
vec4 textureBicubic(sampler2D tex, vec2 uv, vec2 size) {
  vec2 inv = 1.0 / size;
  vec2 t = uv * size - 0.5;
  vec2 fxy = fract(t);
  t -= fxy;
  vec4 xc = cubicW(fxy.x);
  vec4 yc = cubicW(fxy.y);
  vec4 c = t.xxyy + vec2(-0.5, 1.5).xyxy;
  vec4 s = vec4(xc.xz + xc.yw, yc.xz + yc.yw);
  vec4 off = (c + vec4(xc.yw, yc.yw) / s) * inv.xxyy;
  vec4 s0 = texture(tex, off.xz);
  vec4 s1 = texture(tex, off.yz);
  vec4 s2 = texture(tex, off.xw);
  vec4 s3 = texture(tex, off.yw);
  float sx = s.x / (s.x + s.y);
  float sy = s.z / (s.z + s.w);
  return mix(mix(s3, s2, sx), mix(s1, s0, sx), sy);
}

// Pointy-top odd-r lattice (see packages/engine/src/core/hex.ts).
vec2 worldToAxial(vec2 p) {
  vec2 d = p - vec2(HEX_W * 0.5, HEX_SIZE);
  return vec2((0.5773502692 * d.x - d.y / 3.0) / HEX_SIZE, (0.6666666667 * d.y) / HEX_SIZE);
}
vec2 axialToWorld(vec2 a) {
  return vec2(HEX_W * (a.x + a.y * 0.5) + HEX_W * 0.5, HEX_SIZE * 1.5 * a.y + HEX_SIZE);
}
ivec2 axialToOffset(ivec2 a) {
  return ivec2(a.x + (a.y - (a.y & 1)) / 2, a.y);
}
ivec2 cubeRound(vec2 a) {
  vec3 c = vec3(a.x, a.y, -a.x - a.y);
  vec3 r = floor(c + 0.5);
  vec3 d = abs(r - c);
  if (d.x > d.y && d.x > d.z) r.x = -r.y - r.z;
  else if (d.y > d.z) r.y = -r.x - r.z;
  return ivec2(r.xy);
}
`;

/**
 * Organic shoreline. Needs `uNoise` (tileable fBm texture) and `uZoomCss`. Fine octaves fade
 * out when zoomed out so the coast never shimmers.
 */
export const GLSL_COAST = /* glsl */ `
float coastWobble(vec2 p) {
  vec4 m = texture(uNoise, p * (1.0 / 173.0) + vec2(0.37, 0.11));
  vec4 s = texture(uNoise, p * (1.0 / 59.0) + vec2(0.71, 0.53));
  float fine = smoothstep(0.35, 1.2, uZoomCss);
  return (m.g - 0.5) * 13.0 + (m.b - 0.5) * 6.0 + ((s.b - 0.5) * 3.2 + (s.a - 0.5) * 1.8) * fine;
}
`;
